import { IOverlay, IPointGeometry } from "@/types/map";
import * as THREE from "three";

export default class RippleRenderer {
  private view!: __esri.SceneView;
  private externalRenderer!: __esri.externalRenderers;
  private spatialReference!: __esri.SpatialReference;

  private renderer!: THREE.WebGLRenderer;
  private scene!: THREE.Scene;
  private camera!: THREE.Camera;
  private ambient!: THREE.AmbientLight;
  private sun!: THREE.DirectionalLight;

  private rippleObjects: Map<
    string,
    { position: Array<number>; object: THREE.Object3D }
  > = new Map();
  private readonly CylinderHeight = 100;
  private readonly CylinderRadius = 10;

  constructor(
    view: __esri.SceneView,
    externalRenderer: __esri.externalRenderers,
    spatialReference: __esri.SpatialReference
  ) {
    this.view = view;
    this.externalRenderer = externalRenderer;
    this.spatialReference = spatialReference;
  }

  public async setup(context: any) {
    this.renderer = new THREE.WebGLRenderer({
      context: context.gl,
      premultipliedAlpha: false,
    });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setViewport(0, 0, this.view.width, this.view.height);

    this.renderer.autoClear = false;

    const originalSetRenderTarget = this.renderer.setRenderTarget.bind(
      this.renderer
    );
    this.renderer.setRenderTarget = (target) => {
      originalSetRenderTarget(target);
      if (target === null) {
        context.bindRenderTarget();
      }
    };

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera();

    this.ambient = new THREE.AmbientLight(0xffffff, 0.5);
    this.scene.add(this.ambient);
    this.sun = new THREE.DirectionalLight(0xffffff, 0.5);
    this.scene.add(this.sun);

    context.resetWebGLState();
  }

  private animateDir = "up";

  public render(context: any) {
    // update camera parameters
    ///////////////////////////////////////////////////////////////////////////////////
    const cam = context.camera;

    this.camera.position.set(cam.eye[0], cam.eye[1], cam.eye[2]);
    this.camera.up.set(cam.up[0], cam.up[1], cam.up[2]);
    this.camera.lookAt(
      new THREE.Vector3(cam.center[0], cam.center[1], cam.center[2])
    );

    // Projection matrix can be copied directly
    this.camera.projectionMatrix.fromArray(cam.projectionMatrix);

    for (let objectData of this.rippleObjects.values()) {
      const object3D = objectData.object;
      const object3DTransform = object3D.modelViewMatrix;
      object3DTransform.fromArray(
        this.externalRenderer.renderCoordinateTransformAt(
          this.view,
          objectData.position,
          this.spatialReference,
          new Array(16)
        )
      );
      object3DTransform.decompose(
        object3D.position,
        object3D.quaternion,
        new THREE.Vector3(1, 1, 1)
      );
      let renderPos = [0, 0, 0];
      this.externalRenderer.toRenderCoordinates(
        this.view,
        objectData.position,
        0,
        this.spatialReference,
        renderPos,
        0,
        1
      );
      object3D.rotateX(Math.PI / 2);
      object3D.position.set(renderPos[0], renderPos[1], renderPos[2]);

      object3D.scale.x += 0.05;
      object3D.scale.z += 0.05;

      if (object3D.scale.x > 5) {
        object3D.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            ((child as THREE.Mesh).material as THREE.Material).opacity -= 0.05;
          }
        });
      }
      if (object3D.scale.x > 7) {
        object3D.scale.x = 1;
        object3D.scale.z = 1;
        object3D.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            ((child as THREE.Mesh).material as THREE.Material).opacity = 1;
          }
        });
      }
    }

    // draw the scene
    /////////////////////////////////////////////////////////////////////////////////////////////////////
    this.renderer.state.reset();
    this.renderer.render(this.scene, this.camera);

    // as we want to smoothly animate the ISS movement, immediately request a re-render
    this.externalRenderer.requestRender(this.view);

    // cleanup
    context.resetWebGLState();
  }

  public updatePositions(overlays: Array<IOverlay>) {
    overlays.forEach((overlay) => {
      const id = overlay.id || overlay.fields?.id;
      const position = [
        (overlay.geometry as IPointGeometry).x,
        (overlay.geometry as IPointGeometry).y,
        this.CylinderHeight / 2,
      ];

      const objectData = this.rippleObjects.get(id);
      if (objectData) {
        objectData.position = position;
      } else {
        const url = "obj/wall_red.png";
        const textureLoader = new THREE.TextureLoader();
        const map = textureLoader.load(url);
        map.repeat.x = 2;
        const geo = new THREE.CylinderGeometry(
          this.CylinderRadius,
          this.CylinderRadius,
          this.CylinderHeight,
          64,
          1,
          true
        );
        const material = new THREE.MeshStandardMaterial({
          transparent: true,
          map,
          opacity: 1,
        });
        const object = this.transparentObject(geo, material);
        this.rippleObjects.set(id, { position, object });
        this.scene.add(object);
      }
    });
  }

  private transparentObject(
    geometry: THREE.Geometry,
    material: THREE.Material
  ) {
    var obj = new THREE.Object3D();
    var mesh = new THREE.Mesh(geometry, material);
    mesh.material.side = THREE.BackSide; // back faces
    mesh.renderOrder = 0;
    obj.add(mesh);

    var mesh = new THREE.Mesh(geometry, material.clone());
    mesh.material.side = THREE.FrontSide; // front faces
    mesh.renderOrder = 1;
    obj.add(mesh);
    return obj;
  }
}
